import { useState, useRef, ChangeEvent, useCallback, useEffect } from 'react';
import { gql, useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { Link } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';
import SelectInput from './inputs/SelectInput';
import Button from '../components/inputs/Button';
import TextInput from './inputs/TextInput';
import { getEmailUsername, getUserDisplayRole } from '../utils';
import { validateEmailField, validateTel } from '../helpers/validators';
import { USER_DETAILS_QUERY } from '../queries';
import FormItem from './form/FormItem';

function validateTelField(phoneNumber, setError) {
  if (phoneNumber) {
    const valid = validateTel(phoneNumber);
    if (!valid) {
      setError('Invalid phone number.');
      return false;
    }
    setError(null);
    return true;
  }
  setError(null);
  return true;
}

const industries = [
  { value: 'automotive', name: 'Automotive' },
  { value: 'education', name: 'Education' },
  { value: 'financial services', name: 'Financial Services' },
  { value: 'gaming', name: 'Gaming' },
  { value: 'health', name: 'Health' },
  { value: 'insurance', name: 'Insurance' },
  { value: 'investors', name: 'Investors' },
  { value: 'media', name: 'Media' },
  { value: 'political', name: 'Political' },
  { value: 'retail', name: 'Retail' },
  { value: 'b2b', name: 'B2B' },
  { value: 'non-profits', name: 'Non-Profit' },
  { value: 'other', name: 'Other' },
];
const companySizes = [
  { value: '1-10', name: '1-10 Employees' },
  { value: '11-50', name: '11-50 Employees' },
  { value: '51-200', name: '51-200 Employees' },
  { value: '201-500', name: '201-500 Employees' },
  { value: '501-1000', name: '501-1000 Employees' },
  { value: '1001-5000', name: '1001-5000 Employees' },
  { value: '5001-10,000', name: '5001-10,000 Employees' },
  { value: '10,001+', name: '10,001+ Employees' },
];

const UPDATE_USER_MUTATION = gql`
  mutation UpdateUser($userInput: UserInput!) {
    updateUser(userInput: $userInput) {
      message
    }
  }
`;

const UPDATE_USER_EMAIL_MUTATION = gql`
  mutation UpdateUserEmail($newEmail: String!) {
    updateUserEmail(newEmail: $newEmail) {
      email
    }
  }
`;

const UPDATE_USER_AVATAR = gql`
  mutation UpdateUserAvatar($s3Filename: String!) {
    updateUserAvatar(s3Filename: $s3Filename) {
      url
    }
  }
`;

const USER_AVATAR_UPLOAD_URL = gql`
  query UserAvatarUploadUrl($filename: String!) {
    userAvatarUploadUrl(filename: $filename) {
      uploadUrl
      s3Filename
    }
  }
`;

export default function UserProfileDetails() {
  const { user, setUser } = useAuth();
  const { loading, error, data } = useQuery(USER_DETAILS_QUERY);

  const [
    updateUser,
    {
      loading: updateUserLoading,
      error: updateUserError,
      data: updateUserData,
    },
  ] = useMutation(UPDATE_USER_MUTATION, {
    refetchQueries: [USER_DETAILS_QUERY],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setEditing(false);
    },
  });

  const [
    getUserAvatarUploadUrl,
    {
      loading: getUploadUrlLoading,
      error: getUploadUrlError,
      data: uploadUrlData,
    },
  ] = useLazyQuery(USER_AVATAR_UPLOAD_URL);
  const [
    updateUserAvatar,
    {
      loading: updateUserAvatarLoading,
      error: updateUserAvatarError,
      data: updateUserAvatarData,
    },
  ] = useMutation(UPDATE_USER_AVATAR, {
    refetchQueries: [USER_DETAILS_QUERY],
  });

  const [
    updateUserEmail,
    {
      loading: updateUserEmailLoading,
      error: updateUserEmailError,
      data: updateUserEmailData,
    },
  ] = useMutation(UPDATE_USER_EMAIL_MUTATION, {
    refetchQueries: [USER_DETAILS_QUERY],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      setEditingEmail(false);
      setUser((prevState) => ({
        ...prevState,
        email: data.updateUserEmail?.email,
      }));
    },
  });

  const [formState, setFormState] = useState();
  const [editing, setEditing] = useState(false);
  const [validationError, setValidationError] = useState();
  const [avatarValidationError, setAvatarValidationError] = useState<string>();
  const [avatarUploadError, setAvatarUploadError] = useState<string>();
  const [fileToUpload, setFileToUpload] = useState<File>();
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(
    function handleFileToUpload() {
      if (fileToUpload) {
        getUserAvatarUploadUrl({
          variables: {
            filename: fileToUpload.name,
          },
        });
      }
    },
    [fileToUpload]
  );

  useEffect(
    function handleUserAvatarUploadUrl() {
      if (!uploadUrlData && getUploadUrlError) {
        setAvatarUploadError(
          'Failed to get upload URL. Please try again later.'
        );
        setFileToUpload(null);
        setUploadingAvatar(false);
      }

      if (uploadUrlData?.userAvatarUploadUrl?.uploadUrl && fileToUpload) {
        setUploadingAvatar(true);
        const { uploadUrl, s3Filename } = uploadUrlData.userAvatarUploadUrl;

        new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', uploadUrl, true);
          xhr.setRequestHeader('Content-Type', fileToUpload.type);

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(s3Filename);
            } else {
              reject(new Error(`Failed to upload file: ${xhr.statusText}`));
            }
          };

          xhr.onerror = () =>
            reject(new Error('Network error during file upload'));
          xhr.send(fileToUpload);
        })
          .then((s3Filename) => {
            updateUserAvatar({
              variables: {
                s3Filename,
              },
              onCompleted: (data) => {
                if (fileInputRef.current) fileInputRef.current.value = '';
                if (data?.updateUserAvatar?.url) {
                  setUser((prevState) => ({
                    ...prevState,
                    avatar: data.updateUserAvatar.url,
                  }));
                }
              },
            });
          })
          .catch((err) => {
            console.error(err);
            setAvatarUploadError(
              'Failed to upload file. Please try again later.'
            );
          })
          .finally(() => {
            setUploadingAvatar(false);
            setFileToUpload(null);
          });
      }
    },
    [uploadUrlData, fileToUpload]
  );

  const avatarLoading =
    uploadingAvatar || getUploadUrlLoading || updateUserAvatarLoading;
  const avatarError = getUploadUrlError || updateUserAvatarError;

  const [emailFormState, setEmailFormState] = useState();
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailValidationError, setEmailValidationError] = useState();

  const fileInputRef = useRef<HTMLInputElement>();

  const userDetails = data?.userDetails;

  function handleChangeInput(e) {
    const { value, name } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleChangeEmail(e) {
    const { value, name } = e.target;
    setEmailFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function handleChangeSelectInput(clickedVal, name) {
    setFormState((prevState) => ({
      ...prevState,
      [name]: clickedVal,
    }));
  }

  function handleSubmit(e) {
    e.preventDefault();

    if (!editing) {
      const { __typename, avatar, rateCard, ...details } = userDetails;
      let initialState = details;
      Object.keys(initialState).forEach((key) => {
        if (!initialState[key]) {
          initialState[key] = '';
        }
      });
      setFormState(initialState);
      setEditing(true);
      return;
    }

    if (!validateTelField(formState.phoneNumber, setValidationError)) {
      return;
    }

    const { email, rateCard, ...userInput } = formState;
    
    const allowedFields = {
      firstName: userInput.firstName,
      lastName: userInput.lastName,
      phoneNumber: userInput.phoneNumber,
      companyName: userInput.companyName,
      title: userInput.title,
      industry: userInput.industry,
      companySize: userInput.companySize,
    };
    
    const cleanUserInput = Object.fromEntries(
      Object.entries(allowedFields).filter(([_, value]) => value !== undefined)
    );
    
    updateUser({
      variables: {
        userInput: cleanUserInput,
      },
    });
  }

  function handleSubmitEmail(e) {
    e.preventDefault();

    if (!editingEmail) {
      setEmailFormState({ email: userDetails.email });
      setEditingEmail(true);
      return;
    }

    if (!validateEmailField(emailFormState.email, setEmailValidationError)) {
      return;
    }

    updateUserEmail({
      variables: {
        newEmail: emailFormState.email,
      },
    });
  }

  function handleCancel() {
    setEditing(false);
  }

  function handleCancelEditEmail() {
    setEditingEmail(false);
  }

  function handleAvatarSelected({
    target: {
      validity,
      files: [file],
    },
  }: Partial<ChangeEvent<HTMLInputElement>>) {
    setAvatarValidationError(null);
    setAvatarUploadError(null);
    if (!file) return;

    if (file.size > 1000000) {
      setAvatarValidationError('Avatar file size must be less than 1MB.');
      return;
    }
    if (!validity.valid) {
      setAvatarValidationError(
        'File is invalid. Please only upload an image less than 1MB.'
      );
      return;
    }
    setAvatarValidationError(null);
    setFileToUpload(file);
    setUploadingAvatar(true);
  }

  return (
    <>
      {loading && (
        <div className="w-full flex justify-center items-center">
          <LoadingSpinner />
        </div>
      )}
      {error && (
        <div className="mx-auto text-error mt-3 -mb-9">{error.message}</div>
      )}
      {data && !error && (
        <div className="flex flex-col pt-4">
          <div className="flex flex-col items-start mb-8">
            <div className="flex flex-row">
              <div className="flex flex-col">
                <div className="flex flex-col items-center">
                  <div className="flex rounded-full overflow-hidden h-36 w-36 items-center justify-center mb-2">
                    <img
                      className={`object-cover object-center h-full w-full ${
                        avatarLoading ? 'animate-pulse' : ''
                      }`}
                      src={userDetails.avatar}
                      alt="user avatar"
                    />
                  </div>
                  <p className="font-semibold text-lg">
                    {userDetails.firstName ||
                      getEmailUsername(userDetails.email)}
                  </p>
                  {getUserDisplayRole(user) && (
                    <p className="text-sm text-gray-600 mt-1">
                      {getUserDisplayRole(user)}
                    </p>
                  )}
                  {avatarError && (
                    <p className="text-error">{avatarError.message}</p>
                  )}
                  {avatarValidationError && (
                    <p className="text-error">{avatarValidationError}</p>
                  )}
                  {avatarUploadError && (
                    <p className="text-error">{avatarUploadError}</p>
                  )}
                </div>
              </div>
            </div>
            <div className={`flex flex-row w-36 justify-center`}>
              <div className="flex flex-col h-8 items-center">
                <div className="flex flex-col">
                  <label className="cursor-pointer">
                    <input
                      className="w-0 h-0 hidden opacity-0 overflow-hidden"
                      name="avatar"
                      accept="image/*"
                      type="file"
                      onChange={(e) => handleAvatarSelected(e)}
                      ref={fileInputRef}
                    />
                    <div className="font-bold text-blue hover:text-primary">
                      <p className="text-center">
                        {avatarLoading ? `Uploading...` : 'Change photo'}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-row">
            <form className="flex flex-col" onSubmit={handleSubmit}>
              <div className="flex flex-row">
                <div className="flex flex-col space-y-2 mr-2">
                  <div className="flex flex-row space-x-2">
                    <FormItem label="First Name" htmlFor="firstName">
                      <TextInput
                        type="text"
                        name="firstName"
                        value={
                          editing ? formState.firstName : userDetails.firstName
                        }
                        placeholder="First Name"
                        disabled={!editing}
                        onChange={handleChangeInput}
                      />
                    </FormItem>
                    <FormItem label="Last Name" htmlFor="lastName">
                      <TextInput
                        type="text"
                        name="lastName"
                        value={
                          editing ? formState.lastName : userDetails.lastName
                        }
                        placeholder="Last Name"
                        disabled={!editing}
                        onChange={handleChangeInput}
                      />
                    </FormItem>
                  </div>
                  <div className="flex flex-row space-x-2">
                    <FormItem label="Phone Number" htmlFor="phoneNumber">
                      <TextInput
                        type="text"
                        name="phoneNumber"
                        value={
                          editing
                            ? formState.phoneNumber
                            : userDetails.phoneNumber
                        }
                        placeholder="Phone Number"
                        disabled={!editing}
                        onChange={handleChangeInput}
                        onBlur={() =>
                          validateTelField(
                            formState.phoneNumber,
                            setValidationError
                          )
                        }
                        error={validationError}
                      />
                    </FormItem>
                    <FormItem label="Company Name" htmlFor="companyName">
                      <TextInput
                        type="text"
                        name="companyName"
                        value={
                          editing
                            ? formState.companyName
                            : userDetails.companyName
                        }
                        placeholder="Company Name"
                        disabled={!editing}
                        onChange={handleChangeInput}
                      />
                    </FormItem>
                  </div>
                  <div className="flex flex-row space-x-2">
                    <FormItem label="Title" htmlFor="title">
                      <TextInput
                        type="text"
                        name="title"
                        value={editing ? formState.title : userDetails.title}
                        placeholder="Title"
                        disabled={!editing}
                        onChange={handleChangeInput}
                      />
                    </FormItem>
                    <FormItem label="Industry" htmlFor="industry">
                      <SelectInput
                        name="industry"
                        value={
                          editing ? formState.industry : userDetails.industry
                        }
                        placeholder="Industry"
                        disabled={!editing}
                        onChange={(clickedVal) =>
                          handleChangeSelectInput(clickedVal, 'industry')
                        }
                        options={industries}
                      />
                    </FormItem>
                  </div>
                  <div className="flex flex-row space-x-2">
                    <FormItem label="Company Size" htmlFor="companySize">
                      <SelectInput
                        name="companySize"
                        value={
                          editing
                            ? formState.companySize
                            : userDetails.companySize
                        }
                        placeholder="Company Size"
                        disabled={!editing}
                        onChange={(clickedVal) =>
                          handleChangeSelectInput(clickedVal, 'companySize')
                        }
                        options={companySizes}
                      />
                    </FormItem>
                  </div>
                </div>
              </div>
              <div className="flex flex-row pt-5">
                {editing && (
                  <Button
                    className="w-80 mr-2"
                    onClick={handleCancel}
                    type="button"
                    secondary
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  className={`w-80 `}
                  type="submit"
                  secondary={!editing || undefined}
                  primary={editing || undefined}
                  disabled={validationError}
                >
                  {editing
                    ? updateUserLoading
                      ? 'Saving profile'
                      : 'Save profile'
                    : 'Edit profile'}
                </Button>
              </div>
              {updateUserError && !validationError && (
                <div className="mx-auto text-error mt-2 -mb-8">
                  Something went wrong while saving your profile. Please try
                  again later.
                </div>
              )}
              {updateUserData && !updateUserError && (
                <div className="mx-auto text-primary mt-2 -mb-8">
                  Profile updated successfully.
                </div>
              )}
              {validationError && (
                <div className="mx-auto text-error mt-2 -mb-8">
                  {validationError}
                </div>
              )}
            </form>
          </div>
          <div className="flex flex-row mt-14">
            <form className="flex flex-col" onSubmit={handleSubmitEmail}>
              <div className="flex flex-row">
                <div className="flex flex-col space-y-2 mr-2">
                  <div className="flex flex-col">
                    <label
                      className="font-bold text-xs ml-5 text-gray-500"
                      htmlFor="email"
                    >
                      Email
                    </label>
                    <TextInput
                      type="text"
                      name="email"
                      value={
                        editingEmail ? emailFormState.email : userDetails.email
                      }
                      placeholder="Email"
                      disabled={!editingEmail}
                      onChange={handleChangeEmail}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-row pt-5">
                {editingEmail && (
                  <Button
                    className="w-80 mr-2"
                    onClick={handleCancelEditEmail}
                    type="button"
                    secondary
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  className={`w-80 `}
                  type="submit"
                  secondary={!editingEmail || undefined}
                  primary={editingEmail || undefined}
                  disabled={emailValidationError}
                >
                  {editingEmail
                    ? updateUserEmailLoading
                      ? 'Updating email'
                      : 'Update email'
                    : 'Edit email'}
                </Button>
              </div>
              {updateUserEmailError && !emailValidationError && (
                <div className="mx-auto text-error mt-2 -mb-8">
                  {updateUserEmailError?.message
                    ? updateUserEmailError.message
                    : 'Something went wrong while saving your new email. Please try again later.'}
                </div>
              )}
              {updateUserEmailData && !updateUserEmailError && (
                <div className="mx-auto text-primary mt-2 -mb-8">
                  Email updated successfully.
                </div>
              )}
              {emailValidationError && (
                <div className="mx-auto text-error mt-2 -mb-8">
                  {emailValidationError}
                </div>
              )}
            </form>
          </div>
          <div className="flex flex-row mt-14">
            <Link to="/forgotpassword" state={{ email: userDetails.email }}>
              <Button className="w-80" secondary>
                Change password
              </Button>
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
